import type { DishResponse } from "api-types";

import type { MenuFilterState } from "./filter-state-type";

/**
 * The picked tags a dish must carry. An avoid tag is a warning the restaurant
 * attaches, not something to ask for, so it never makes a dish suitable.
 */
const requiredTags = (filters: MenuFilterState) =>
  filters.selectedTags.filter(({ displayIntent }) => displayIntent !== "avoid");

/** Whether the guest has picked a diet or any dish tag. */
export const hasDietSelection = (filters: MenuFilterState): boolean =>
  filters.selectedPreference !== null || requiredTags(filters).length > 0;

/**
 * Whether the dish carries everything the guest picked: the diet, if any, and
 * every tag. Picking Vegan and Halal asks for dishes that are both.
 */
export const meetsDietSelection = (
  dish: DishResponse,
  filters: MenuFilterState
): boolean => {
  const { selectedPreference } = filters;
  const carriesPreference =
    !selectedPreference ||
    (dish.preferences ?? []).some(
      ({ igfdTaxPreferenceId }) =>
        igfdTaxPreferenceId === selectedPreference.igfdTaxPreferenceId
    );
  const dishTagIds = new Set((dish.tags ?? []).map(({ id }) => id));
  return (
    carriesPreference &&
    requiredTags(filters).every(({ id }) => dishTagIds.has(id))
  );
};

/** Whether the guest has asked the menu anything at all. */
export const hasActiveGuestFilters = (filters: MenuFilterState): boolean =>
  filters.allergens.some((allergen) => allergen.selected) ||
  filters.ingredients.some((ingredient) => ingredient.selected) ||
  hasDietSelection(filters);

/**
 * Whether they are filtering on something that can hurt them. Red is the
 * allergen colour: a missed dietary preference is a choice, not a risk.
 */
export const hasAllergenFilters = (filters: MenuFilterState): boolean =>
  filters.allergens.some((allergen) => allergen.selected) ||
  filters.ingredients.some((ingredient) => ingredient.selected);
